'use client'

import { MouseEvent, useState } from 'react'

import { Icon } from '@/app/components/Icon'
import { useSocialShare } from '@/app/hooks/useSocialShare'
import { cn } from '@/app/utils/style'
import { LocalIconName } from '@/icons'
import { FontAwesomeStyle } from '@/lib/fontawesome-generated'

type Platform = 'facebook' | 'twitter' | 'linkedin' | 'instagram' | 'link'

type IconConfig =
  | {
      provider: 'local'
      name: LocalIconName
    }
  | {
      provider: 'fontawesome'
      name: string
      style: FontAwesomeStyle
    }

export interface SocialShareProps {
  className?: string
  label?: string
  platforms?: Platform[]
  shareOnPrefix?: string
  linkCopiedLabel?: string
}

const platformIconMap: Record<Platform, IconConfig> = {
  facebook: { provider: 'local', name: 'facebook' },
  twitter: { provider: 'local', name: 'twitter' },
  linkedin: { provider: 'local', name: 'linkedin' },
  instagram: { provider: 'local', name: 'instagram' },
  link: { provider: 'fontawesome', name: 'faLink', style: 'regular' },
}

export const SocialShare = ({
  className,
  label = 'Share:',
  platforms = ['link', 'instagram', 'facebook', 'twitter', 'linkedin'],
  shareOnPrefix = 'Share on',
  linkCopiedLabel = 'Link copied!',
}: SocialShareProps) => {
  const { handleClick } = useSocialShare()
  const [showCopiedTooltip, setShowCopiedTooltip] = useState(false)

  if (!platforms?.length) return null

  const handleLinkClick = (event: MouseEvent<HTMLSpanElement>, platform: Platform) => {
    handleClick(event, platform)

    if (platform === 'link') {
      setShowCopiedTooltip(true)
      setTimeout(() => setShowCopiedTooltip(false), 2000)
    }
  }

  return (
    <div
      className={cn('flex flex-row gap-xl rounded-xl bg-neutral-50 py-xl items-center', className)}
    >
      {label && (
        <p className="size-subheading-sm font-bold whitespace-nowrap text-neutral-900">{label}</p>
      )}
      <div className="flex flex-row gap-lg">
        {platforms.map((platform) => {
          const iconConfig = platformIconMap[platform]

          return (
            <span
              key={platform}
              className="relative flex cursor-pointer items-center justify-center"
              onClick={(event) => handleLinkClick(event, platform)}
              role="button"
              tabIndex={0}
              aria-label={`${shareOnPrefix} ${platform}`}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleLinkClick(e as any, platform)
                }
              }}
            >
              <Icon
                {...iconConfig}
                size={20}
                className="text-neutral-900 transition-opacity hover:opacity-70"
              />
              {platform === 'link' && showCopiedTooltip && (
                <span className="absolute -top-[40px] left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md bg-blue-100 px-2 py-1 text-xs text-neutral-900 font-bold opacity-100">
                  {linkCopiedLabel}
                </span>
              )}
            </span>
          )
        })}
      </div>
    </div>
  )
}
